import React, { FC, useContext, useEffect, useMemo } from 'react';
import { useIsMobile } from '@sravni/react-utils';
import { StepContext } from '../../../creator/step.context';
import { useFormContext } from 'react-hook-form';
import { Rows, Row, Col } from '../../../../landing.common';
import { FocusFieldWrapper, StepContainer, StepSubmitBlock } from '../common';
import {
    EmploymentTypeField,
    OrganizationNameFieldContainer,
    JobTitleFieldContainer,
    UnEmploymentReasonFieldContainer,
    WorkPhoneNumberFieldContainer,
    JobTypeFieldContainer,
    SalaryFieldContainer,
    LastExperienceFieldsContainer,
    AnotherUnemploymentReasonFieldContainer,
} from './fields';
import { FORM_FIELDS, FORM_STEPS } from '../../../../../constants';
import { needed as fieldNeeded } from '../../../steps/work/dependencies';
import { DesktopSubmitView } from '../../components/form/components/desktop.submit';
import { EMPLOYMENT_VALUES, UNEMPLOYMENT_REASONS } from '../../../../../constants/sources';
import { WorkFieldsContainer } from './components/desktop';

export const WorkStepView: FC = React.memo(() => {
    const isMobile = useIsMobile();
    const { currentStep, currentStepConfig } = useContext(StepContext);

    const form = useFormContext();

    const {
        watch,
        formState: { dirtyFields: _ },
        setFocus,
    } = form;

    const [employmentType, unemploymentReason] = watch([FORM_FIELDS.EMPLOYMENT_TYPE, FORM_FIELDS.UNEMPLOYMENT_REASON]);

    useEffect(() => {
        if (FORM_STEPS.PERSONAL_DATA === currentStepConfig.stepName) {
            if (!isMobile) {
                try {
                    setFocus(FORM_FIELDS.EMPLOYMENT_TYPE);
                } catch (e) {}
            }
        }
    }, [currentStep]);

    const isUnemploymentOtherReason = useMemo(
        () =>
            Boolean(
                fieldNeeded(FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON, employmentType) &&
                    unemploymentReason === UNEMPLOYMENT_REASONS.OTHER,
            ),
        [employmentType, unemploymentReason],
    );

    const showJobFieldsRow = useMemo(
        () => employmentType && employmentType !== EMPLOYMENT_VALUES.RETIRED,
        [employmentType],
    );

    return (
        <StepContainer>
            <Rows gap="16">
                <Row gap="16" mobileGap="12">
                    <Col md-4>
                        <FocusFieldWrapper name={FORM_FIELDS.EMPLOYMENT_TYPE} field={<EmploymentTypeField />} />
                    </Col>
                    {fieldNeeded(FORM_FIELDS.ORGANIZATION_NAME, employmentType) && (
                        <Col md-4>
                            <FocusFieldWrapper
                                name={FORM_FIELDS.ORGANIZATION_NAME}
                                field={<OrganizationNameFieldContainer />}
                            />
                        </Col>
                    )}
                    {fieldNeeded(FORM_FIELDS.LAST_EXPERIENCE_START_MONTH, employmentType) && (
                        <Col md-4>
                            <LastExperienceFieldsContainer />
                        </Col>
                    )}
                </Row>
                <WorkFieldsContainer />
                {showJobFieldsRow && (
                    <Row gap="16" mobileGap="12">
                        {fieldNeeded(FORM_FIELDS.WORK_PHONE, employmentType) && (
                            <Col md-4>
                                <FocusFieldWrapper
                                    name={FORM_FIELDS.WORK_PHONE}
                                    field={<WorkPhoneNumberFieldContainer />}
                                />
                            </Col>
                        )}
                        {fieldNeeded(FORM_FIELDS.UNEMPLOYMENT_REASON, employmentType) && (
                            <Col md-4>
                                <FocusFieldWrapper
                                    name={FORM_FIELDS.UNEMPLOYMENT_REASON}
                                    field={<UnEmploymentReasonFieldContainer />}
                                />
                            </Col>
                        )}
                        {isUnemploymentOtherReason && (
                            <Col md-4>
                                <FocusFieldWrapper
                                    name={FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON}
                                    field={<AnotherUnemploymentReasonFieldContainer />}
                                />
                            </Col>
                        )}
                        {fieldNeeded(FORM_FIELDS.JOB_TITLE, employmentType) && (
                            <Col md-4>
                                <FocusFieldWrapper name={FORM_FIELDS.JOB_TITLE} field={<JobTitleFieldContainer />} />
                            </Col>
                        )}
                        {fieldNeeded(FORM_FIELDS.JOB_TYPE, employmentType) && (
                            <Col md-4>
                                <FocusFieldWrapper name={FORM_FIELDS.JOB_TYPE} field={<JobTypeFieldContainer />} />
                            </Col>
                        )}
                    </Row>
                )}

                <Row gap="16" mobileGap="12">
                    <Col md-4>
                        <FocusFieldWrapper name={FORM_FIELDS.MONTHLY_SALARY} field={<SalaryFieldContainer />} />
                    </Col>
                </Row>
            </Rows>
            <StepSubmitBlock>
                <DesktopSubmitView nextBtnText={currentStepConfig.submitBtnText} />
            </StepSubmitBlock>
        </StepContainer>
    );
});

WorkStepView.displayName = 'WorkStepView';
